import { createContext, useContext, useId } from 'react';
import { useFocusRing } from '@react-aria/focus';
import {
  GroupField,
  GroupFieldItem,
  validGroupFieldProps,
} from '../GroupField/GroupField';
import {
  RadioGroupItemProps,
  RadioGroupItemType,
  RadioGroupProps,
  RadioGroupState,
} from './types';
import { useGroupState } from '../../groupUtils';
import { FocusableRadioIcon } from './FocusableRadioIcon';
import { pick } from 'lodash';
import styled from 'styled-components';
import { Text } from '@ardoq/typography';
import { RadioGroupItemTypeWrapper } from './RadioGroupItemTypeWrapper';
import { Box, FlexBox } from '@ardoq/layout';
import { UnstyledButton } from '@ardoq/button';

const RadioGroupItemContainer = styled(FlexBox)`
  &[aria-disabled='true'] {
    pointer-events: none;
  }
`;

const RadioGroupItemHelperTextContainer = styled(Box).attrs({
  marginBottom: 'small',
  marginLeft: 'xlarge',
})`
  max-width: 255px;
`;

const RadioGroupContext = createContext<RadioGroupState>({});

export const RadioGroup = ({ children, ...props }: RadioGroupProps) => {
  const { options = [], groupItemType } = props;
  const state = useGroupState(props);

  const checkboxId = useId();
  const labelId = props.label
    ? props.labelId || `checkbox-${checkboxId}-label`
    : undefined;

  return (
    <RadioGroupContext.Provider value={state}>
      <GroupField
        role="radiogroup"
        {...pick(props, validGroupFieldProps)}
        labelId={labelId}
        aria-labelledby={labelId}
      >
        {options.map(({ label, ...rest }) => (
          <RadioGroupItem
            key={rest.value}
            groupItemType={groupItemType}
            {...rest}
          >
            {label}
          </RadioGroupItem>
        ))}
        {children}
      </GroupField>
    </RadioGroupContext.Provider>
  );
};

export const RadioGroupItem = ({
  value,
  children,
  media,
  isDisabled: isDisabledOption,
  groupItemType = RadioGroupItemType.DEFAULT,
  helperText,
  ...rest
}: RadioGroupItemProps & {
  groupItemType?: RadioGroupItemType;
}) => {
  const {
    setCurrentValue,
    currentValue,
    hasError,
    hasWarning,
    isDisabled: isDisabledField,
  } = useContext(RadioGroupContext);
  const { isFocusVisible, focusProps } = useFocusRing();
  const isChecked = currentValue === value;
  const isDisabled = isDisabledOption || isDisabledField;
  return (
    <UnstyledButton
      onClick={() => !isDisabled && setCurrentValue?.(value)}
      style={{ display: 'flex' }}
    >
      <RadioGroupItemContainer
        aria-disabled={isDisabled}
        aria-checked={isChecked}
        aria-describedby={rest['aria-describedby']}
        role="radio"
        style={{
          cursor: !isDisabled ? 'pointer' : 'auto',
        }}
      >
        <RadioGroupItemTypeWrapper
          media={media}
          isChecked={isChecked}
          isDisabled={isDisabled}
          groupItemType={groupItemType}
        >
          <GroupFieldItem
            $hasError={hasError}
            $hasWarning={hasWarning}
            $isDisabled={isDisabled}
            $isActive={isChecked}
            tabIndex={isDisabled ? -1 : 0}
            {...focusProps}
            {...rest}
            onKeyDown={e => {
              // spacebar
              if (e.key === ' ') {
                e.preventDefault();
                setCurrentValue?.(value);
              }
            }}
          >
            <FlexBox gap="small" align="center">
              <FocusableRadioIcon
                isChecked={isChecked}
                isFocused={isFocusVisible}
              />
              {children}
            </FlexBox>
          </GroupFieldItem>
          {helperText && (
            <RadioGroupItemHelperTextContainer>
              <Text
                variant="caption"
                color={isDisabled ? 'textDisabled' : 'textSubtle'}
              >
                {helperText}
              </Text>
            </RadioGroupItemHelperTextContainer>
          )}
        </RadioGroupItemTypeWrapper>
      </RadioGroupItemContainer>
    </UnstyledButton>
  );
};
